import { RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useUpdatedRef from '../../../../utils/react/useUpdatedRef';
import { closeSheet, completeSheetCloseTransition, openSheet, updateSheetActiveSnapPoint } from '../sheetActions';
import { getIsSheetOpen, getSheetActiveSnapPoint, getSheetData, getSheetKey } from '../sheetSelectors';
import { SheetCloseOptions, SheetId, SheetOpenOptions } from '../sheetTypes';

export type UseSheetOptions = {
    openByDefault?: boolean;
    /** Also available as an `onCloseTransitionEnd` prop on `<Sheet>`; both run if both are given. */
    onCloseTransitionEnd?: () => void;
};

/**
 * The props `<Sheet>` needs, plus the pieces of sheet state the rest of the app reads.
 * Spread the whole thing onto `<Sheet>` — it takes what it needs and ignores the rest.
 */
export type UseSheetReturnType = {
    sheetId: SheetId;
    sheetKey: string;
    data?: object;

    isOpen: boolean;
    openSheet: (options?: SheetOpenOptions) => void;
    closeSheet: (options?: SheetCloseOptions) => void;

    contentRef: RefObject<HTMLDivElement>;

    // --- Roma Sheet props ---
    onOpenChange: (isOpen: boolean) => void;
    activeSnapPoint: number;
    onSnapPointChange: (activeSnapPoint: number) => void;

    /** The redux teardown for a finished close, run by `<Sheet>` ahead of any `onCloseTransitionEnd`. */
    completeCloseTransition: () => void;
};

/**
 * Translates the app's redux sheet state into the controlled props the Roma `<Sheet>` expects.
 */
const useSheet = (
    sheetId: SheetId,

    // Needed when multiple instances of the same sheet exist (e.g. element-specific sheets)
    instanceId?: string,

    { openByDefault = false, onCloseTransitionEnd }: UseSheetOptions = {},
): UseSheetReturnType => {
    const contentRef = useRef<HTMLDivElement>(null);
    const sheetKey = getSheetKey(sheetId, instanceId);

    const isOpen = useSelector(getIsSheetOpen(sheetId, instanceId));
    const activeSnapPoint = useSelector(getSheetActiveSnapPoint(sheetId, instanceId));
    const data = useSelector(getSheetData(sheetId, instanceId));

    const dispatch = useDispatch();

    // Consumers almost always pass a fresh closure, so a ref keeps the callback stable
    const onCloseTransitionEndRef = useUpdatedRef(onCloseTransitionEnd);

    const dispatchOpenSheet = useCallback(
        (options?: SheetOpenOptions) => dispatch(openSheet(sheetId, instanceId, options)),
        [dispatch, sheetId, instanceId],
    );

    const dispatchCloseSheet = useCallback(
        (options?: SheetCloseOptions) => dispatch(closeSheet(sheetId, instanceId, options)),
        [dispatch, sheetId, instanceId],
    );

    const onOpenChange = useCallback(
        (shouldBeOpen: boolean) => (shouldBeOpen ? dispatchOpenSheet() : dispatchCloseSheet()),
        [dispatchOpenSheet, dispatchCloseSheet],
    );

    const onSnapPointChange = useCallback(
        (newActiveSnapPoint: number) => dispatch(updateSheetActiveSnapPoint(newActiveSnapPoint, sheetId, instanceId)),
        [dispatch, sheetId, instanceId],
    );

    const completeCloseTransition = useCallback(() => {
        dispatch(completeSheetCloseTransition(sheetKey));
        onCloseTransitionEndRef.current?.();
    }, [dispatch, sheetKey]);

    // Deliberately not keyed on isOpen — the sheet opens once, and stays closed if the user closes it
    useEffect(() => {
        if (openByDefault && !isOpen) dispatchOpenSheet();
    }, [openByDefault]);

    return useMemo(
        () => ({
            sheetId,
            sheetKey,
            data,

            isOpen,
            openSheet: dispatchOpenSheet,
            closeSheet: dispatchCloseSheet,

            contentRef,

            onOpenChange,
            activeSnapPoint,
            onSnapPointChange,
            completeCloseTransition,
        }),
        [
            sheetId,
            sheetKey,
            data,
            isOpen,
            dispatchOpenSheet,
            dispatchCloseSheet,
            onOpenChange,
            activeSnapPoint,
            onSnapPointChange,
            completeCloseTransition,
        ],
    );
};

export default useSheet;
